import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Star, Minus, Plus, ShoppingCart, Heart, Truck, Shield, RefreshCw, 
  ChevronLeft, ChevronRight, Check, ChevronDown, Home, Package,
  Clock, Wallet, Award, ThumbsUp, User, Camera, X, ZoomIn
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProductPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [cartItems, setCartItems] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [reviewSort, setReviewSort] = useState('newest');
  const [zoomedImage, setZoomedImage] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [showZoom, setShowZoom] = useState(false);
  const imageRef = useRef(null);
  
  // Accordion states
  const [openSections, setOpenSections] = useState({ description: true });

  useEffect(() => {
    fetchProduct();
    loadCart();
    window.scrollTo(0, 0);
  }, [id]);

  const loadCart = () => {
    const saved = localStorage.getItem('indicraft_cart');
    if (saved) setCartItems(JSON.parse(saved));
  };

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products/${id}`);
      setProduct(response.data);

      // Fetch reviews
      const reviewsRes = await axios.get(`${API_URL}/api/products/${id}/reviews`);
      setReviews(reviewsRes.data || []);

      // Fetch related products
      if (response.data.category_id) {
        const relatedRes = await axios.get(`${API_URL}/api/products?category=${response.data.category_id}`);
        setRelatedProducts(relatedRes.data.filter(p => p.id !== id).slice(0, 8));
      }
    } catch (error) {
      console.error('Failed to fetch product:', error);
      toast.error('Product not found');
      navigate('/shop');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    const existing = cartItems.find(item => item.id === product.id);
    let newCart;
    if (existing) {
      newCart = cartItems.map(item => 
        item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item
      );
    } else {
      newCart = [...cartItems, { ...product, quantity }];
    }
    setCartItems(newCart);
    localStorage.setItem('indicraft_cart', JSON.stringify(newCart));
    toast.success(`${product.name} added to cart!`);
  };

  const buyNow = () => {
    addToCart();
    navigate('/checkout');
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleMouseMove = (e) => {
    if (!imageRef.current) return;
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setMousePosition({ x, y });
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (reviewSort === 'newest') return new Date(b.created_at) - new Date(a.created_at);
    if (reviewSort === 'highest') return b.rating - a.rating;
    if (reviewSort === 'lowest') return a.rating - b.rating;
    return 0;
  });

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : product?.rating || 4.5;

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex flex-col">
        <Header />
        <div className="flex-grow flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-maroon/10 flex items-center justify-center mx-auto mb-4 animate-pulse">
              <span className="text-3xl text-maroon font-accent">ॐ</span>
            </div>
            <p className="text-maroon/60 font-medium">Loading product...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!product) return null;

  const accordionSections = [
    { 
      id: 'description', 
      title: 'Product Description', 
      content: product.description,
      icon: Package
    },
    { 
      id: 'specifications', 
      title: 'Specifications', 
      content: product.specifications || product.features?.map(f => `• ${f}`).join('\n') || 'Premium quality puja item crafted with devotion.',
      icon: Award
    },
    { 
      id: 'shipping', 
      title: 'Shipping & Returns', 
      content: product.shipping_info || 'Free shipping on orders above ₹499. Easy 7-day returns. COD available on all orders.',
      icon: Truck
    },
    { 
      id: 'warranty', 
      title: 'Warranty Information', 
      content: product.warranty_info || 'Quality assured. Contact us within 7 days of delivery for any manufacturing defects.',
      icon: Shield
    },
    { 
      id: 'faq', 
      title: 'Frequently Asked Questions', 
      content: product.faq || 'Q: Is this item authentic?\nA: Yes, all our products are 100% authentic and sourced from trusted artisans.\n\nQ: How long does delivery take?\nA: Usually 5-7 business days across India.',
      icon: ThumbsUp
    },
  ];

  return (
    <div className="min-h-screen bg-cream flex flex-col">
      <Header cartCount={cartItems.reduce((sum, item) => sum + item.quantity, 0)} />
      
      <main className="flex-grow">
        {/* Breadcrumb */}
        <div className="bg-white border-b border-gold/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <nav className="flex items-center gap-2 text-sm text-maroon/60">
              <button onClick={() => navigate('/')} className="hover:text-maroon flex items-center gap-1">
                <Home className="w-4 h-4" /> Home
              </button>
              <ChevronRight className="w-4 h-4" />
              <button onClick={() => navigate('/shop')} className="hover:text-maroon">Shop</button>
              <ChevronRight className="w-4 h-4" />
              <span className="text-maroon font-medium truncate max-w-[200px]">{product.name}</span>
            </nav>
          </div>
        </div>

        {/* Main Product Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
            {/* Image Gallery */}
            <div className="space-y-4">
              {/* Main Image with Zoom */}
              <div 
                ref={imageRef}
                className="relative aspect-square rounded-2xl overflow-hidden bg-white border border-gold/10 cursor-zoom-in group"
                onMouseMove={handleMouseMove}
                onMouseEnter={() => setShowZoom(true)}
                onMouseLeave={() => setShowZoom(false)}
                onClick={() => setZoomedImage(product.images?.[selectedImage])}
              >
                <img
                  src={product.images?.[selectedImage] || 'https://via.placeholder.com/600'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
                
                {/* Zoom indicator */}
                <div className="absolute bottom-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-sm text-maroon">
                  <ZoomIn className="w-4 h-4" /> Click to zoom
                </div>

                {/* Zoom lens effect */}
                {showZoom && (
                  <div 
                    className="absolute w-32 h-32 border-2 border-gold rounded-full pointer-events-none hidden lg:block"
                    style={{
                      left: `${mousePosition.x}%`,
                      top: `${mousePosition.y}%`,
                      transform: 'translate(-50%, -50%)',
                      backgroundImage: `url(${product.images?.[selectedImage]})`,
                      backgroundSize: '400%',
                      backgroundPosition: `${mousePosition.x}% ${mousePosition.y}%`,
                    }}
                  />
                )}
                
                {product.discount_percentage > 0 && (
                  <div className="absolute top-4 left-4">
                    <span className="bg-saffron text-white font-bold px-3 py-1.5 rounded-full shadow-lg">
                      {product.discount_percentage}% OFF
                    </span>
                  </div>
                )}

                {/* Navigation arrows */}
                {product.images?.length > 1 && (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev > 0 ? prev - 1 : product.images.length - 1); }}
                      className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronLeft className="w-5 h-5 text-maroon" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedImage(prev => prev < product.images.length - 1 ? prev + 1 : 0); }}
                      className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/90 backdrop-blur-sm rounded-full flex items-center justify-center shadow-lg hover:bg-white transition-colors"
                    >
                      <ChevronRight className="w-5 h-5 text-maroon" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {product.images?.length > 1 && (
                <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                  {product.images.map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                        selectedImage === index ? 'border-gold shadow-lg scale-105' : 'border-transparent opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Details */}
            <div className="lg:sticky lg:top-24 lg:self-start">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-maroon mb-3">
                {product.name}
              </h1>

              {/* Short Description */}
              {product.short_description && (
                <p className="text-maroon/70 mb-4">{product.short_description}</p>
              )}

              {/* Rating */}
              <div className="flex items-center gap-3 mb-4">
                <div className="flex items-center gap-1 bg-green-600 text-white px-2 py-1 rounded">
                  <span className="font-bold">{averageRating}</span>
                  <Star className="w-4 h-4 fill-white" />
                </div>
                <span className="text-maroon/60">{reviews.length || product.reviews_count} Reviews</span>
              </div>

              {/* Price Section */}
              <div className="bg-gradient-to-r from-cream to-gold/5 rounded-xl p-4 mb-4">
                <div className="flex items-baseline gap-3 mb-1">
                  <span className="text-3xl md:text-4xl font-bold text-maroon">₹{product.discount_price}</span>
                  {product.original_price > product.discount_price && (
                    <>
                      <span className="text-lg text-maroon/50 line-through">₹{product.original_price}</span>
                      <span className="bg-green-100 text-green-700 font-semibold px-2 py-0.5 rounded text-sm">
                        Save ₹{(product.original_price - product.discount_price).toFixed(0)}
                      </span>
                    </>
                  )}
                </div>
                <p className="text-sm text-maroon/60">Inclusive of all taxes</p>
              </div>

              {/* Stock Status */}
              <div className="flex items-center gap-2 mb-4">
                {product.stock > 0 ? (
                  <span className="flex items-center gap-1 text-green-600 font-medium">
                    <Check className="w-4 h-4" /> In Stock
                  </span>
                ) : (
                  <span className="text-red-500 font-medium">Out of Stock</span>
                )}
                {product.stock > 0 && product.stock <= 10 && (
                  <span className="text-orange-500 text-sm">Only {product.stock} left!</span>
                )}
              </div>

              {/* Delivery Estimate */}
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl mb-4">
                <Truck className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-medium text-blue-900">{product.delivery_estimate || 'Delivery in 5-7 business days'}</p>
                  <p className="text-sm text-blue-600">Free shipping on orders above ₹499</p>
                </div>
              </div>

              {/* COD Badge */}
              {product.cod_available !== false && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-xl mb-4">
                  <Wallet className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-700">Cash on Delivery Available</span>
                </div>
              )}

              {/* Quantity & Add to Cart */}
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-maroon">Quantity:</span>
                  <div className="flex items-center gap-2 bg-white rounded-full border border-gold/20 p-1">
                    <button
                      onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
                      className="w-10 h-10 rounded-full bg-cream hover:bg-gold/10 flex items-center justify-center transition-colors"
                      data-testid="decrease-qty"
                    >
                      <Minus className="w-4 h-4 text-maroon" />
                    </button>
                    <span className="w-12 text-center font-semibold text-maroon text-lg">{quantity}</span>
                    <button
                      onClick={() => setQuantity(prev => Math.min(product.stock, prev + 1))}
                      className="w-10 h-10 rounded-full bg-cream hover:bg-gold/10 flex items-center justify-center transition-colors"
                      data-testid="increase-qty"
                    >
                      <Plus className="w-4 h-4 text-maroon" />
                    </button>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    onClick={addToCart}
                    disabled={product.stock === 0}
                    className="flex-1 bg-maroon hover:bg-maroon-dark text-white font-semibold py-6 rounded-xl text-lg transition-all"
                    data-testid="add-to-cart-btn"
                  >
                    <ShoppingCart className="w-5 h-5 mr-2" />
                    Add to Cart
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-14 h-14 rounded-xl border border-maroon/20 hover:bg-maroon hover:text-white flex-shrink-0"
                  >
                    <Heart className="w-5 h-5" />
                  </Button>
                </div>

                <Button
                  onClick={buyNow}
                  disabled={product.stock === 0}
                  className="w-full bg-gold hover:bg-gold-dark text-maroon-dark font-semibold py-6 rounded-xl text-lg transition-all"
                  data-testid="buy-now-btn"
                >
                  Buy Now
                </Button>
              </div>

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-3 p-4 bg-white rounded-xl border border-gold/10">
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-1">
                    <Shield className="w-5 h-5 text-gold" />
                  </div>
                  <p className="text-xs font-medium text-maroon">100% Authentic</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-1">
                    <RefreshCw className="w-5 h-5 text-gold" />
                  </div>
                  <p className="text-xs font-medium text-maroon">Easy Returns</p>
                </div>
                <div className="text-center">
                  <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center mx-auto mb-1">
                    <Award className="w-5 h-5 text-gold" />
                  </div>
                  <p className="text-xs font-medium text-maroon">Secure Payment</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Collapsible Product Details - Accordion */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl border border-gold/10 overflow-hidden">
            {accordionSections.map((section, index) => {
              const Icon = section.icon;
              const isOpen = openSections[section.id];
              return (
                <div key={section.id} className={index > 0 ? 'border-t border-gold/10' : ''}>
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-5 hover:bg-cream/50 transition-colors"
                    data-testid={`accordion-${section.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-gold" />
                      <span className="font-heading font-semibold text-maroon">{section.title}</span>
                    </div>
                    <ChevronDown className={`w-5 h-5 text-maroon transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isOpen && (
                    <div className="px-5 pb-5 pt-0">
                      <div className="pl-8 text-maroon/70 whitespace-pre-line leading-relaxed">
                        {section.content}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {/* Product Detail Images */}
        {product.detail_images?.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-heading font-bold text-maroon mb-6">Product Details</h2>
            <div className="space-y-4">
              {product.detail_images.map((img, index) => (
                <img 
                  key={index} 
                  src={img} 
                  alt={`${product.name} detail ${index + 1}`}
                  className="w-full rounded-2xl"
                />
              ))}
            </div>
          </section>
        )}

        {/* Reviews Section */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="reviews-section">
          <div className="bg-white rounded-2xl border border-gold/10 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
              <div>
                <h2 className="text-2xl font-heading font-bold text-maroon">Customer Reviews</h2>
                <div className="flex items-center gap-3 mt-2">
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-5 h-5 ${i < Math.floor(averageRating) ? 'fill-gold text-gold' : 'text-gold/30'}`} />
                    ))}
                  </div>
                  <span className="font-bold text-maroon">{averageRating}</span>
                  <span className="text-maroon/60">({reviews.length} reviews)</span>
                </div>
              </div>
              <select
                value={reviewSort}
                onChange={(e) => setReviewSort(e.target.value)}
                className="px-4 py-2 rounded-lg border border-gold/20 bg-cream text-maroon"
              >
                <option value="newest">Newest First</option>
                <option value="highest">Highest Rating</option>
                <option value="lowest">Lowest Rating</option>
              </select>
            </div>

            {sortedReviews.length === 0 ? (
              <div className="text-center py-12">
                <Star className="w-12 h-12 text-gold/30 mx-auto mb-3" />
                <p className="text-maroon/60">No reviews yet. Be the first to review!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {sortedReviews.map((review) => (
                  <div key={review.id} className="p-4 bg-cream rounded-xl">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-maroon/10 flex items-center justify-center">
                          <User className="w-5 h-5 text-maroon" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-maroon">{review.customer_name}</span>
                            {review.verified && (
                              <span className="flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                                <Check className="w-3 h-3" /> Verified
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-gold text-gold' : 'text-gold/30'}`} />
                              ))}
                            </div>
                            <span className="text-xs text-maroon/50">
                              {new Date(review.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <p className="text-maroon/70 ml-13 pl-13">{review.review_text}</p>
                    {review.images?.length > 0 && (
                      <div className="flex gap-2 mt-3 ml-13">
                        {review.images.map((img, i) => (
                          <img 
                            key={i} 
                            src={img} 
                            alt="" 
                            className="w-16 h-16 rounded-lg object-cover cursor-pointer hover:opacity-80"
                            onClick={() => setZoomedImage(img)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h2 className="text-2xl font-heading font-bold text-maroon mb-6">You May Also Like</h2>
            <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
              {relatedProducts.map(prod => (
                <div
                  key={prod.id}
                  className="flex-shrink-0 w-48 md:w-56 bg-white rounded-2xl overflow-hidden border border-transparent hover:border-gold/30 shadow-sm hover:shadow-lg transition-all cursor-pointer group"
                  onClick={() => { navigate(`/product/${prod.id}`); window.scrollTo(0, 0); }}
                  data-testid={`related-product-${prod.id}`}
                >
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={prod.images?.[0] || 'https://via.placeholder.com/400'}
                      alt={prod.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-3">
                    <h3 className="font-medium text-maroon mb-1 line-clamp-2 text-sm">{prod.name}</h3>
                    <div className="flex items-center gap-1 mb-1">
                      <Star className="w-3 h-3 fill-gold text-gold" />
                      <span className="text-xs text-maroon">{prod.rating}</span>
                    </div>
                    <div className="flex items-baseline gap-2">
                      <span className="font-bold text-maroon">₹{prod.discount_price}</span>
                      {prod.original_price > prod.discount_price && (
                        <span className="text-xs text-maroon/50 line-through">₹{prod.original_price}</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Fullscreen Image Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setZoomedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 rounded-full flex items-center justify-center text-white hover:bg-white/20"
            onClick={() => setZoomedImage(null)}
          >
            <X className="w-6 h-6" />
          </button>
          <img 
            src={zoomedImage} 
            alt="Zoomed" 
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ProductPage;
